import { useState } from "react";

import ComponentHeader from "../../../../../components/ComponentHeader";
import Circle from "../../../../../components/AgGrid/CellRenderers/Circle";
import { ConfirmationDialog } from "../../../../../components/ConfirmationDialog";
import OnboardingFilterInputs from "../FiltersInputs";
import AddClientDialog from "../AddClientDialog";
import MoveClientsDialog from "../MoveClientsDialog";

import useOnboardingWallet from "../../../../../hooks/SalesBonusApi/useOnboardingWallet";
import useMutation from "../../../../../hooks/useMutate";

import { IOnboardingWallet } from "../../../../../interfaces/SalesBonus/IWallets";
import { IOnboardingWalletsQueryOptions } from "../../../../../interfaces/SalesBonus/QueriesOptions/IWalletsQueryOptions";

import { Box, Pagination, Stack } from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import MoneyOffOutlinedIcon from "@mui/icons-material/MoneyOffOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import MoveDownOutlinedIcon from "@mui/icons-material/MoveDownOutlined";

import OnboardingWalletApi from "../../../../../services/SalesBonusApi/onboardingWallet";
import DataGrid from "../../../../../components/DataGrid";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid";

const onboardingWalletApi = new OnboardingWalletApi();

const OnboardingWallet = () => {
    const [onboardingWalletParams, setOnboardingWalletParams] =
        useState<IOnboardingWalletsQueryOptions>({
            page: 1,
            onboarding__in: undefined,
            paying_off: true,
            active: true,
            order_by: "updated_at",
        });

    const [openAddClientDialog, setOpenAddClientDialog] = useState(false);
    const [
        openChangePayingOffDialogConfirmartion,
        setOpenChangePayingOffDialogConfirmartion,
    ] = useState(false);
    const [openChangeActiveDialog, setOpenChangeActiveDialog] = useState(false);
    const [clientsToMove, setClientsToMove] = useState<IOnboardingWallet[]>([]);
    const [openMoveClientsDialog, setOpenMoveClientsDialog] = useState(false);

    const gridRef = useGridApiRef();

    const { mutate: changeOnboardingWallet } = useMutation({
        successText: "Registro alterado.",
        errorText: "Erro ao alterar registro.",
        queryKeyArray: ["onboarding-wallet"],
        callback: onboardingWalletApi.put,
    });

    const [columnDefs] = useState<GridColDef<IOnboardingWallet>[]>([
        {
            field: "onboarding",
            minWidth: 180,
            headerName: "Responsável",
            flex: 1,
        },
        {
            field: "cnpj",
            minWidth: 180,
            headerName: "CNPJ",
            flex: 1,
        },
        {
            field: "restaurant_name",
            minWidth: 300,
            headerName: "Nome do Restaurante",
            flex: 1,
        },
        {
            field: "paying_off",
            minWidth: 200,
            headerName: "Contando no mês",
            renderCell: (params) => {
                return Circle({ value: params.row.paying_off });
            },
        },
        {
            field: "active",
            width: 100,
            headerName: "Ativo",
            renderCell: (params) => {
                return Circle({ value: params.row.active });
            },
        },
    ]);

    const {
        data: onboardingWalletResponse,
        isFetching: gettingOnboardingWalletResponse,
    } = useOnboardingWallet(onboardingWalletParams);

    const handleChangePayingOff = () => {
        const selectedRows = gridRef.current.getSelectedRows();
        const selectedRowsArray = Array.from(
            selectedRows.values()
        ) as IOnboardingWallet[];
        const dataWithPayingOffChanged = selectedRowsArray.map((record) => ({
            ...record,
            paying_off: !onboardingWalletParams.paying_off,
        }));
        changeOnboardingWallet({
            object: dataWithPayingOffChanged,
        });
    };

    const handleChangeActive = () => {
        const selectedRows = gridRef.current.getSelectedRows();
        const selectedRowsArray = Array.from(
            selectedRows.values()
        ) as IOnboardingWallet[];
        const dataWithActiveChanged = selectedRowsArray.map((record) => ({
            ...record,
            active: !onboardingWalletParams.active,
        }));
        changeOnboardingWallet({
            object: dataWithActiveChanged,
        });
    };

    const handleOpenMoveClientsDialog = () => {
        const selectedRows = gridRef.current.getSelectedRows();
        const selectedRowsArray = Array.from(
            selectedRows.values()
        ) as IOnboardingWallet[];
        setClientsToMove(selectedRowsArray);
        setOpenMoveClientsDialog(true);
    };

    return (
        <Box>
            <ComponentHeader.Root>
                <ComponentHeader.Title text="Carteira Onboarding" />
                <ComponentHeader.Actions>
                    {onboardingWalletParams.paying_off ? (
                        <ComponentHeader.Action
                            tooltip="Desconsiderar registros."
                            icon={
                                <MoneyOffOutlinedIcon
                                    fontSize="large"
                                    color="error"
                                />
                            }
                            action={() => {
                                setOpenChangePayingOffDialogConfirmartion(true);
                            }}
                        />
                    ) : (
                        <ComponentHeader.Action
                            tooltip="Considerar registros."
                            icon={
                                <CurrencyExchangeOutlinedIcon
                                    fontSize="large"
                                    color="primary"
                                />
                            }
                            action={() => {
                                setOpenChangePayingOffDialogConfirmartion(true);
                            }}
                        />
                    )}
                    {onboardingWalletParams.active ? (
                        <ComponentHeader.Action
                            tooltip="Desativar clientes."
                            icon={
                                <DeleteForeverOutlinedIcon
                                    fontSize="large"
                                    color="error"
                                />
                            }
                            action={() => {
                                setOpenChangeActiveDialog(true);
                            }}
                        />
                    ) : (
                        <ComponentHeader.Action
                            tooltip="Ativar clientes."
                            icon={
                                <RestoreFromTrashOutlinedIcon
                                    fontSize="large"
                                    color="primary"
                                />
                            }
                            action={() => {
                                setOpenChangeActiveDialog(true);
                            }}
                        />
                    )}
                    <ComponentHeader.Action
                        tooltip="Mover clientes."
                        icon={
                            <MoveDownOutlinedIcon
                                fontSize="large"
                                color="primary"
                            />
                        }
                        action={() => {
                            handleOpenMoveClientsDialog();
                        }}
                    />
                    <ComponentHeader.Action
                        tooltip="Adicionar cliente."
                        icon={
                            <AddCircleOutlineOutlinedIcon
                                fontSize="large"
                                color="info"
                            />
                        }
                        action={() => {
                            setOpenAddClientDialog(true);
                        }}
                    />
                </ComponentHeader.Actions>
            </ComponentHeader.Root>
            <OnboardingFilterInputs
                queryParams={onboardingWalletParams}
                setQueryParams={setOnboardingWalletParams}
            />

            <Box width={"100%"} height={"500px"}>
                <DataGrid<IOnboardingWallet>
                    apiRef={gridRef}
                    columns={columnDefs}
                    rows={
                        onboardingWalletResponse?.results
                            ? onboardingWalletResponse?.results
                            : []
                    }
                    loading={gettingOnboardingWalletResponse}
                    isRowSelectable={() => true}
                    checkboxSelection
                />
                {onboardingWalletResponse && (
                    <Box
                        display={"flex"}
                        marginTop={"10px"}
                        justifyContent={"end"}
                    >
                        <Stack spacing={2}>
                            <Pagination
                                count={Math.ceil(
                                    onboardingWalletResponse?.info.count /
                                        onboardingWalletResponse?.info.per_page
                                )}
                                page={onboardingWalletParams.page}
                                onChange={(e, value) =>
                                    setOnboardingWalletParams((prevState) => ({
                                        ...prevState,
                                        page: value,
                                    }))
                                }
                                shape="rounded"
                            />
                        </Stack>
                    </Box>
                )}
            </Box>
            {openAddClientDialog && (
                <AddClientDialog
                    setOpenDialog={setOpenAddClientDialog}
                    openDialog={openAddClientDialog}
                    queryKey={["onboarding-wallet"]}
                    callback={onboardingWalletApi.post}
                />
            )}
            {openChangePayingOffDialogConfirmartion && (
                <ConfirmationDialog
                    setOpenDialog={setOpenChangePayingOffDialogConfirmartion}
                    openDialog={openChangePayingOffDialogConfirmartion}
                    title={`Deseja mesmo ${
                        onboardingWalletParams.paying_off
                            ? "desconsiderar"
                            : "considerar"
                    } os clientes?`}
                    callback={handleChangePayingOff}
                />
            )}
            {openChangeActiveDialog && (
                <ConfirmationDialog
                    setOpenDialog={setOpenChangeActiveDialog}
                    openDialog={openChangeActiveDialog}
                    title={`Deseja mesmo ${
                        onboardingWalletParams.active ? "desativar" : "reativar"
                    } os clientes?`}
                    callback={handleChangeActive}
                />
            )}
            {openMoveClientsDialog && (
                <MoveClientsDialog
                    setOpenDialog={setOpenMoveClientsDialog}
                    openDialog={openMoveClientsDialog}
                    clients={clientsToMove}
                />
            )}
        </Box>
    );
};

export default OnboardingWallet;
